import { useGate, useStore } from 'effector-react'
import { $totalCount } from 'features/cars-list/model'
import { CitySelect } from 'features/city-select/components'
import {
  $cities,
  $cityWasJustAutoDetected,
  $fetching as $citiesFetching,
  acceptAutoDetectedCity,
  fetchCitiesFx,
  getNearest
} from 'features/city-select/model'
import { FilterSection } from 'features/filter/components/filter-section'
import {
  $declaration,
  $defaultFilterValues,
  $filterValues,
  $isFilterReady,
  $permanentFilterValues,
  fetchDeclarationFx,
  FilterValues,
  hasCitySelection as hasCitySelectionGate,
  resetValues
} from 'features/filter/model'
import { createMediaStyles } from 'lib/styled-helpers'
import { useCarsFoundText } from 'lib/use-cars-found-text'
import React, { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import ReactTooltip from 'react-tooltip'
import { useAVNConfig } from 'stages/AVN'
import styled from 'styled-components'
import { Button } from 'ui/button'
import { Icon } from 'ui/icon'
import { SkeletonBlock } from 'ui/skeleton-block'

import { getCities } from '../../../api/cities'
import { getProjectName } from '../../../lib/get-project-name'
import { getFromStorage } from '../../../lib/storage'
import { AVNStageProps } from '../../../stages/AVN/types'
import { ModalWindow } from '../../../ui/modal-window'

type Props = {
  apiUrl: string
  carsFoundForms: CarsFoundForms
  maxVisibleCount?: number
  filterValues?: FilterValues
  filterSectionsClosed?: boolean
  filterSectionsClosedMobile?: boolean
  forwardedFilterOptions: AVNStageProps['filterOptions']
  i18n: AVNStageProps['i18n']
  permanentFilterValues: AVNStageProps['permanentFilter']
  hasCitySelection: boolean
}

export type CarsFoundForms = [string, string, string?]

export const Filter: FC<{}> = () => {
  const {
    getFilterUrl: apiUrl,
    maxVisibleCount,
    filterValues,
    filterSectionsClosed = false,
    filterSectionsClosedMobile = false,
    filterOptions: forwardedFilterOptions,
    permanentFilter: permanentFilterValues = {},
    i18n,
    citySelect,
    isHideCollapseFilterSection
  } = useAVNConfig()

  const [expanded, setExpanded] = useState(false)
  const [selecting, setSelecting] = useState(false)
  const [confirmCity, setConfirmCity] = useState(true)
  const [whereAreYou, setWhereAreYou] = useState(false)
  const cityWasJustAutoDetected = useStore($cityWasJustAutoDetected)

  useGate($defaultFilterValues, filterValues)
  useGate($permanentFilterValues, permanentFilterValues)
  useGate(hasCitySelectionGate, { enabled: !!citySelect })

  const carsFound = useStore($totalCount)
  const declaration = useStore($declaration)
  const citiesAreReady = useStore($citiesFetching)
  const filterIsReady = useStore($isFilterReady)
  const allCities = useStore($cities)

  useEffect(() => {
    fetchDeclarationFx({ url: apiUrl, filterOptions: forwardedFilterOptions })
  }, [apiUrl])
  useEffect(() => {
    if (allCities.length > 1 && citySelect && citySelect.useGeolocation) {
      setTimeout(() => {
        setConfirmCity(false)
      }, 5000)
    }
  }, [cityWasJustAutoDetected])

  const carsFoundText = useCarsFoundText(carsFound, maxVisibleCount, i18n.carsFoundForms)
  /*TODO если разработка, то берем url из моков, если тест или прод, то составляем из частей , так как в моках не приходит весь url*/
  const url_from_nearest =
    process.env.NODE_ENV === 'development'
      ? citySelect && citySelect.getCitiesUrl
      : citySelect && document.location.origin + citySelect.getCitiesUrl
  const citiesUrl = url_from_nearest && new URL(url_from_nearest)

  if (!filterIsReady) {
    if (citiesAreReady) {
      return (
        <>
          {citySelect && (
            <CityWrapper>
              <CitySelect {...citySelect} />
            </CityWrapper>
          )}
          <SkeletonBlock height={'800px'} />
        </>
      )
    } else {
      return <SkeletonBlock height={'800px'} />
    }
  }

  const startSelecting = () => {
    setSelecting(true)
    ReactTooltip.hide()
  }
  const CityConfirmation = () => (
    <StyledCityConfirmation>
      <StyledCityConfirmationInner>
        <City>
          {getFromStorage('citySelectselectedCities') + ' — '}
          {citySelect && citySelect.i18n.isItYourCity}
        </City>
        <WrapButton>
          <Button onClick={() => acceptAutoDetectedCity()} uppercase={true} textSize={'10px'}>
            {citySelect && citySelect.i18n.yesItIsMyCity}
          </Button>
          <ButtonGrey
            onClick={() => {
              if (citySelect && citySelect.useGeolocation) {
                setConfirmCity(true)
                setWhereAreYou(true)
              } else {
                /*Старое поведение*/
                startSelecting()
              }
            }}
          >
            {citySelect && citySelect.i18n.noItIsNotMyCity}
          </ButtonGrey>
        </WrapButton>
      </StyledCityConfirmationInner>
    </StyledCityConfirmation>
  )

  return (
    <>
      {getProjectName() === 'haval' && (
        <ModalWindow onCloseRequest={() => setConfirmCity(true)} visible={!confirmCity && cityWasJustAutoDetected}>
          <CityConfirmationWrap>{CityConfirmation()}</CityConfirmationWrap>
        </ModalWindow>
      )}
      {getProjectName() === 'haval' &&
        createPortal(
          <ModalWindow
            visible={whereAreYou}
            onCloseRequest={() => setWhereAreYou(false)}
            title={'Текущее местоположение не определено'}
            positionTitle={citySelect && citySelect.useGeolocation ? 'left' : 'center'}
            sizeTextTitle={'22px'}
            paddingTop={'0'}
            paddingLeft={'2.4rem'}
            paddingRight={'2.4rem'}
            paddingBottom={'2.4rem'}
            useGeolocation={citySelect && citySelect.useGeolocation}
          >
            <Text>Разрешите определить ваше местоположение, чтобы найти ближайший дилерский центр</Text>
            <ButtonWrapper>
              <Button
                uppercase={true}
                onClick={() => {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      citiesUrl && citiesUrl.searchParams.append('latitude', String(position.coords.latitude))
                      citiesUrl && citiesUrl.searchParams.append('longitude', String(position.coords.longitude))
                      citiesUrl &&
                        fetchCitiesFx(citiesUrl.href).then((response) => {
                          getNearest(response.nearest)
                        })
                      setWhereAreYou(false)
                    },
                    () => {
                      /*Юзер отклонил определение местоположения или произошла ошибка определения*/
                      setWhereAreYou(false)
                      startSelecting()
                    }
                  )
                }}
              >
                Разрешить
              </Button>
              <Button
                text={true}
                uppercase={true}
                onClick={() => {
                  setWhereAreYou(false)
                  startSelecting()
                }}
              >
                Не сейчас
              </Button>
            </ButtonWrapper>
          </ModalWindow>,
          document.body
        )}
      <Wrapper>
        <OpenButtonContainer>
          <Button size={'small'} block={true} onClick={() => setExpanded((showFilterMobile) => !showFilterMobile)}>
            <OpenButtonContent>
              <LeftButtonContent>{i18n.openFilter}</LeftButtonContent>
            </OpenButtonContent>
          </Button>
        </OpenButtonContainer>
        {citySelect && (
          <CityWrapper className={'city-select-mobile'}>
            <CitySelect {...citySelect} selecting={selecting} setSelecting={setSelecting} />
          </CityWrapper>
        )}
        <FilterWrapper expanded={expanded}>
          {carsFound !== null && <CarsCount data-name="cars-count">{carsFoundText}</CarsCount>}
          {citySelect && (
            <CityWrapper className={'city-select-desktop'}>
              <CitySelect {...citySelect} selecting={selecting} setSelecting={setSelecting} />
            </CityWrapper>
          )}
          {declaration?.sections.map((section) => (
            <FilterSection
              key={section.key}
              declaration={section}
              sectionClosed={filterSectionsClosed}
              sectionClosedMobile={filterSectionsClosedMobile}
              hideCollapse={section.hideCollapse ? section.hideCollapse : false}
              isHideCollapseFilterSection={section.key === 'labels' && isHideCollapseFilterSection}
            />
          ))}
          <ResetButtonContainer>
            {getProjectName() === 'avito' && carsFound !== null && (
              <CarsCount data-name="cars-count" className={'count-bottom-filter'}>
                {carsFoundText}
              </CarsCount>
            )}
            <Button
              size={'medium'}
              block={true}
              onClick={() => resetValues()}
              data-name="reset-button"
              customSize={getProjectName() === 'avito' ? '48px' : null}
              textButton={getProjectName() === 'avito'}
            >
              {i18n.resetFilter}
            </Button>
          </ResetButtonContainer>
        </FilterWrapper>
      </Wrapper>
    </>
  )
}

const Wrapper = styled.div`
  width: 100%;
  max-width: 100%;
  background: ${({ theme }) => theme.palette.white};
  border-radius: ${() => getProjectName() === 'avito' && '12px'};
`

const CityWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: ${({ theme }) => `1px solid ${theme.palette.grey[200]}`};
  position: relative;
  &.city-select-mobile {
    ${createMediaStyles(() => ({
      tablet: 'display: none;',
      phone: 'display: block'
    }))}
  }
  &.city-select-desktop {
    ${createMediaStyles(() => ({
      tablet: 'display: block;',
      phone: 'display: none'
    }))}
  }
`

const ResetButtonContainer = styled.div`
  display: flex;
  flex-direction: ${() => getProjectName() === 'avito' && 'column'};
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]};
  .count-bottom-filter {
    margin-bottom: 2px;
  }
`

const OpenButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]};

  ${createMediaStyles(() => ({
    tablet: 'display: none;',
    phone: 'margin-bottom: 10px'
  }))}
`

const OpenButtonContent = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const LeftButtonContent = styled.div`
  ${({ theme }) => theme.textStyles.p3};
  font-weight: 700;
  ${() => getProjectName() === 'avito' && 'width: 100%; font-size: 14px; font-weight: 500;'}
`

const FilterWrapper = styled.div<{ expanded: boolean }>`
  display: ${({ expanded }) => (expanded ? 'block' : 'none')};

  ${createMediaStyles(() => ({
    tablet: 'display: block;',
    phone: 'margin-bottom: 10px'
  }))}
`

const CarsCount = styled.div`
  display: none;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]} ${theme.spacing[0]}`};
  text-align: center;
  color: ${({ theme }) => (getProjectName() === 'avito' ? theme.palette.black : theme.palette.grey[500])};
  ${({ theme }) => theme.textStyles.p2};

  ${createMediaStyles(() => ({
    tablet: 'display: block;'
  }))}
`

const CityConfirmationWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  ${({ theme }) => getProjectName() === 'avito' && theme.textStyles.p1};
  font-weight: 500;
  box-shadow: 4px 4px 8px rgb(0 0 0 / 10%);
`
const StyledCityConfirmation = styled.div`
  cursor: default;
  ${({ theme }) => theme.textStyles.p2};
  ${createMediaStyles(() => ({
    phone: 'font-size: 1.6rem;',
    tablet: 'font-size: 2.2rem;'
  }))}
  font-weight: 500;
  button {
    margin-right: 15px;
  }
  width: 100%;
`
const StyledCityConfirmationInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${createMediaStyles(() => ({
    phone: 'padding: 2rem;',
    tablet: 'padding: 0'
  }))}
`
const Text = styled.div`
  font-size: 16px;
  margin-bottom: 27px;

  ${createMediaStyles(() => ({
    phone: 'padding-right: 0;',
    tablet: 'padding-right: 175px;'
  }))}
`
const ButtonWrapper = styled.div`
  display: flex;
  button {
    margin-right: 10px;
  }
`
const City = styled.span`
  margin-top: 1.6rem;
  ${createMediaStyles(() => ({
    phone: 'font-size: 1.6rem;',
    tablet: 'font-size: 2.2rem;'
  }))}
  color: #222222;
`
const WrapButton = styled.div`
  display: flex;
  margin-top: 2rem;
`
const ButtonGrey = styled.div`
  background-color: #cccccd;
  color: #000001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  text-transform: uppercase;
  font-size: 1.6rem;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    background-color: ${({ theme }) => theme.palette.brand.primary};
    color: ${({ theme }) => theme.palette.white};
  }
`
